'use client';

import type { FC } from 'react';
import { m, useReducedMotion } from 'motion/react';
import { usePathname } from 'next/navigation';
import { twMerge } from 'tailwind-merge';

import { Heading } from '@/components/ui/heading/heading';

import { AccentImage } from '../../accent-image/accent-image';
import type { TApiHeading, TApiImage, TApiText } from '@/shared/apiComponentTypes';
import { getImageSource } from '@/shared/image';
import { fadeSlideUp, fadeSlideLeft, fadeSlideRight, staggerContainer } from '@/utils/animation/variants';

export type TImageSection = [TApiImage, TApiHeading, TApiText];

export const ImageSection: FC<
  TImageSection & { shiftRight?: boolean; headingAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span'; animateOnLoad?: boolean }
> = ({ headingAs = 'h2', animateOnLoad = false, ...props }) => {
  const sectionImage = getImageSource(props[0].src, 'medium');
  const prefersReducedMotion = useReducedMotion();
  const pathname = usePathname();

  // Use different animation direction based on image position
  const imageVariant = props.shiftRight ? fadeSlideRight : fadeSlideLeft;
  const contentVariant = props.shiftRight ? fadeSlideLeft : fadeSlideUp;

  return (
    <m.div
      key={pathname}
      initial={prefersReducedMotion ? false : 'hidden'}
      {...(animateOnLoad ? { animate: 'visible' } : { whileInView: 'visible', viewport: { once: true, amount: 0.2 } })}
      variants={staggerContainer}
      className="flex flex-col md:flex-row items-center gap-10"
    >
      <m.div
        variants={imageVariant}
        className={twMerge('basis-1/2 rounded-md', props.shiftRight && 'md:order-1')}
      >
        {sectionImage && (
          <AccentImage
            src={sectionImage.url}
            alt={sectionImage.alt}
            width={sectionImage.width}
            height={sectionImage.height}
            className="max-h-96"
          />
        )}
      </m.div>
      <m.div variants={contentVariant} className="basis-1/2">
        <div className="inline-block mb-2 text-xs font-oswald uppercase tracking-[0.35em] text-neutral-400">
          <span className="font-oswald">{props[1].caption}</span>
          <div className="flex items-center gap-2 mt-2 w-3/4">
            <div className="h-1 basis-1/3 rounded-full bg-action" />
            <div className="h-[2px] basis-2/3 rounded-full bg-neutral-700/70" />
          </div>
        </div>

        <Heading variant={headingAs} className="mb-10" highlight={props[1].highlight}>
          {props[1].title}
        </Heading>

        <p className="mb-10 text-neutral-300">{props[2].text}</p>
      </m.div>
    </m.div>
  );
};
